import { ChartData, ChartDataset, Chart as ChartJS, ChartOptions, Legend, LinearScale, LineElement, PointElement, ScatterController, Tooltip } from "chart.js";
import React, { useEffect, useMemo, useState } from "react";
import { Scatter } from "react-chartjs-2";
import { createChartAreaImagePlugin } from "./ChartBackgroundImagePlugin";

// Register necessary Chart.js components
ChartJS.register(ScatterController, LinearScale, PointElement, Tooltip, Legend, LineElement);

export type ChartBounds = {
    minX?: number;
    maxX?: number;
    minY?: number;
    maxY?: number;
};

export type DataPoint = {
    x: number;
    y: number;
    z: number;
    [key: string]: any; // Allow additional metadata
};

export type DatasetConfig = {
    label: string;
    data: DataPoint[];
    color: string;
    pointRadius?: number;
    borderWidth?: number;
    borderColor?: string;
    showLine?: boolean;
    fill?: boolean;
    tension?: number;
    stepped?: boolean;
    opacity?: number;
    pointStyle?: "circle" | "triangle";
};

export type TargetDatasetConfig = {
    label: string;
    data: DataPoint[];
    baseColor: string;
    selectedColor: string;
    baseRadius?: number;
    selectedRadius?: number;
    selectedIndexes?: number[];
    pointStyle?: "circle" | "triangle";
    borderColor?: string;
    borderWidth?: number;
    selectedBorderWidth?: number;
};

export type DataChartProps = {
    datasets?: DatasetConfig[];
    targetDatasets?: TargetDatasetConfig[];
    bounds?: ChartBounds;
    xAxisLabel?: string;
    yAxisLabel?: string;
    onPointClick?: (datasetIndex: number, pointIndex: number, point: DataPoint) => void;
    onHover?: (position: { x: number; y: number } | null) => void;
    showCursorPosition?: boolean;
    showLegend?: boolean;
    height?: string;
    devicePosition?: { x: number; y: number };
    deviceHeading?: number;
    backgroundImageUrl?: string;
    coordinateTransform?: (points: { x: number; y: number; z?: number }[]) => { x: number; y: number; z?: number }[];
    customTooltipCallback?: (point: DataPoint, datasetLabel: string) => string;
};

export const DataChart: React.FC<DataChartProps> = (props) => {
    const [scatterData, setScatterData] = useState<ChartData<"scatter", DataPoint[]>>();
    const [options, setOptions] = useState<ChartOptions<"scatter">>();
    const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null);
    const datasets: Array<ChartDataset<"scatter", DataPoint[]>> = [];

    // Prepare the scatter plot data
    useEffect(() => {

        // Add regular datasets
        if (props.datasets) {
            props.datasets.forEach(dataset => {
                const color = dataset.opacity !== undefined
                    ? `${dataset.color}${Math.round(dataset.opacity * 255).toString(16).padStart(2, '0')}`
                    : dataset.color;

                datasets.push({
                    label: dataset.label,
                    data: dataset.data,
                    backgroundColor: color,
                    borderColor: color,
                    pointRadius: dataset.pointRadius ?? 4,
                    borderWidth: dataset.borderWidth ?? 0,
                    showLine: dataset.showLine ?? false,
                    fill: dataset.fill ?? false,
                    pointHoverRadius: dataset.showLine ? 0 : undefined,
                    tension: dataset.tension ?? 0,
                    stepped: dataset.stepped ?? false,
                    pointStyle: dataset.pointStyle ?? "circle"
                } as any);
            });
        }

        // Add target datasets with selection support
        if (props.targetDatasets) {
            props.targetDatasets.forEach(targetDataset => {
                datasets.push({
                    label: targetDataset.label,
                    data: targetDataset.data,
                    backgroundColor: targetDataset.data.map((_, index) => {
                        const isSelected = targetDataset.selectedIndexes?.includes(index);
                        return isSelected ? targetDataset.selectedColor : targetDataset.baseColor;
                    }),
                    pointRadius: targetDataset.data.map((_, index) => {
                        const isSelected = targetDataset.selectedIndexes?.includes(index);
                        return isSelected
                            ? (targetDataset.selectedRadius ?? 8)
                            : (targetDataset.baseRadius ?? 5);
                    }),
                    pointStyle: targetDataset.pointStyle ?? "circle",
                    borderWidth: targetDataset.data.map((_, index) => {
                        const isSelected = targetDataset.selectedIndexes?.includes(index);
                        return isSelected
                            ? (targetDataset.selectedBorderWidth ?? 3)
                            : (targetDataset.borderWidth ?? 0);
                    }),
                    borderColor: targetDataset.borderColor ?? "#FFFFFF"
                } as any);
            });
        }

        setScatterData({ datasets });
    }, [props.datasets, props.targetDatasets, props.coordinateTransform]);

    // Chart options
    useEffect(() => {
        setOptions({
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            onHover: (event, elements, chart) => {
                if ((props.showCursorPosition || props.onHover) && event.native && chart.scales.x && chart.scales.y) {
                    const rect = chart.canvas.getBoundingClientRect();
                    const mouseEvent = event.native as MouseEvent;
                    const x = mouseEvent.clientX - rect.left;
                    const y = mouseEvent.clientY - rect.top;

                    const dataX = chart.scales.x.getValueForPixel(x);
                    const dataY = chart.scales.y.getValueForPixel(y);

                    if (dataX !== undefined && dataY !== undefined) {
                        const position = { x: dataX, y: dataY };
                        if (props.showCursorPosition) {
                            setCursorPosition(position);
                        }
                        props.onHover?.(position);
                    }
                } else {
                    setCursorPosition(null);
                    props.onHover?.(null);
                }
            },
            onClick: (event, elements) => {
                if (elements.length > 0 && props.onPointClick) {
                    const element = elements[0];
                    const datasetIndex = element.datasetIndex;
                    const pointIndex = element.index;
                    const dataset = scatterData?.datasets[datasetIndex];

                    if (dataset) {
                        const point = dataset.data[pointIndex];
                        props.onPointClick(datasetIndex, pointIndex, point);
                    }
                }
            },
            scales: {
                x: {
                    type: "linear",
                    position: "bottom",
                    title: {
                        display: true,
                        text: props.xAxisLabel ?? "X-Axis (meters)",
                    },
                    min: props.bounds?.minX,
                    max: props.bounds?.maxX
                },
                y: {
                    type: "linear",
                    title: {
                        display: true,
                        text: props.yAxisLabel ?? "Y-Axis (meters)",
                    },
                    min: props.bounds?.minY,
                    max: props.bounds?.maxY
                },
            },
            plugins: {
                tooltip: {
                    callbacks: {
                        label: (context) => {
                            const data = context.raw as any;
                            const dataset = scatterData?.datasets[context.datasetIndex];

                            // Use custom tooltip callback if provided
                            if (props.customTooltipCallback) {
                                return props.customTooltipCallback(data, dataset?.label || "");
                            }

                            // Default tooltip formatting
                            if (data.targetId !== undefined) {
                                return `${dataset?.label} - ID: ${data.targetId} - (${data.x?.toFixed(2)}, ${data.y?.toFixed(2)})`;
                            } else {
                                return `${dataset?.label}: (${data.x?.toFixed(2)}, ${data.y?.toFixed(2)})`;
                            }
                        },
                    },
                },
                legend: {
                    display: props.showLegend ?? true,
                    position: 'top'
                }
            },
        });
    }, [props.bounds, props.xAxisLabel, props.yAxisLabel, props.onPointClick, props.onHover, props.showCursorPosition, props.showLegend, props.customTooltipCallback, scatterData]);

    // Keep the plugin array stable per URL. Also key the Scatter on the URL so
    // Chart.js cleanly tears down and rebuilds when the background changes —
    // react-chartjs-2 does not reliably sync the plugins array to an existing
    // chart instance, which left stale background images across tab switches.
    const backgroundPlugins = useMemo(
        () => createChartAreaImagePlugin(props.backgroundImageUrl ?? ""),
        [props.backgroundImageUrl]
    );

    return (
        <div
            style={{
                height: props.height ?? "500px",
                width: "100%",
                position: "relative"
            }}
            onMouseLeave={() => {
                setCursorPosition(null);
                props.onHover?.(null);
            }}
        >
            {scatterData && (
                <Scatter
                    key={props.backgroundImageUrl ?? "__no-bg__"}
                    data={scatterData}
                    options={options}
                    plugins={backgroundPlugins}
                />
            )}
            {/* Cursor Position Display */}
            {props.showCursorPosition && cursorPosition && (
                <div
                    style={{
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        color: "white",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontFamily: "monospace",
                        pointerEvents: "none",
                        zIndex: 1000
                    }}
                >
                    X: {cursorPosition.x.toFixed(2)}m, Y: {cursorPosition.y.toFixed(2)}m
                </div>
            )}
        </div>
    );
};
